import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { AdminService } from "@/gen/omnios/v1/admin_connect.js";
import { formatCurrency } from "@/lib/format";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Outlet,
	createFileRoute,
	useMatches,
	useNavigate,
} from "@tanstack/react-router";
import { Loader2, Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/admin/billing")({
	component: BillingLayout,
});

function BillingLayout() {
	const matches = useMatches();
	const hasChild = matches.some((m) => m.routeId === "/admin/billing/$id");
	if (hasChild) return <Outlet />;
	return <BillingPage />;
}

const adminClient = createClient(AdminService, transport);

function BillingPage() {
	const { t } = useTranslation("admin");
	useBreadcrumbs([
		{ label: t("admin_root"), href: "/admin" },
		{ label: t("billing_title") },
	]);
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [createOpen, setCreateOpen] = useState(false);

	const { data, isLoading } = useQuery({
		queryKey: ["admin", "billing-plans"],
		queryFn: () => adminClient.listBillingPlans({}),
	});

	const plans = data?.plans ?? [];

	const invalidate = () =>
		queryClient.invalidateQueries({ queryKey: ["admin", "billing-plans"] });

	if (isLoading) {
		return (
			<div className="space-y-4">
				<Skeleton className="h-8 w-48" />
				<Skeleton className="h-64" />
			</div>
		);
	}

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-end">
				<Dialog open={createOpen} onOpenChange={setCreateOpen}>
					<DialogTrigger asChild>
						<Button size="sm">
							<Plus className="h-4 w-4 mr-1.5" />
							{t("billing_create_plan")}
						</Button>
					</DialogTrigger>
					<DialogContent>
						<CreatePlanForm
							onSuccess={(planId) => {
								setCreateOpen(false);
								invalidate();
								if (planId)
									navigate({
										to: "/admin/billing/$id",
										params: { id: planId },
									});
							}}
						/>
					</DialogContent>
				</Dialog>
			</div>

			{plans.length === 0 ? (
				<p className="text-sm text-muted-foreground py-8 text-center">
					{t("billing_no_plans")}
				</p>
			) : (
				<div className="rounded-lg border overflow-hidden">
					<Table>
						<TableHeader>
							<TableRow className="bg-muted/30">
								<TableHead>{t("billing_col_name")}</TableHead>
								<TableHead className="w-28">{t("billing_col_slug")}</TableHead>
								<TableHead className="w-28 text-right">
									{t("billing_monthly")}
								</TableHead>
								<TableHead className="w-28 text-right">
									{t("billing_annual")}
								</TableHead>
								<TableHead className="w-20 text-right">
									{t("billing_col_trial")}
								</TableHead>
								<TableHead className="w-20 text-center">
									{t("billing_col_status")}
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{plans.map((plan) => {
								const monthly = plan.prices.find((p) => p.period === "monthly");
								const annual = plan.prices.find((p) => p.period === "annual");
								return (
									<TableRow
										key={plan.id}
										className="cursor-pointer hover:bg-muted/50"
										onClick={() =>
											navigate({
												to: "/admin/billing/$id",
												params: { id: plan.id },
											})
										}
									>
										<TableCell>
											<span className="font-medium">{plan.name}</span>
											{plan.description && (
												<span className="text-muted-foreground ml-2 text-xs">
													{plan.description}
												</span>
											)}
										</TableCell>
										<TableCell>
											<code className="text-xs">{plan.slug}</code>
										</TableCell>
										<TableCell className="text-right tabular-nums">
											{monthly ? formatCurrency(monthly.baseAmount) : "—"}
										</TableCell>
										<TableCell className="text-right tabular-nums">
											{annual ? formatCurrency(annual.baseAmount) : "—"}
										</TableCell>
										<TableCell className="text-right tabular-nums">
											{plan.trialDays > 0 ? `${plan.trialDays}д` : "—"}
										</TableCell>
										<TableCell className="text-center">
											<Badge
												variant={plan.isPublic ? "default" : "secondary"}
												className="text-[10px]"
											>
												{plan.isPublic
													? t("billing_plan_public")
													: t("billing_plan_hidden")}
											</Badge>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</div>
			)}
		</div>
	);
}

function CreatePlanForm({
	onSuccess,
}: { onSuccess: (planId: string) => void }) {
	const { t } = useTranslation("admin");
	const [slug, setSlug] = useState("");
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [isPublic, setIsPublic] = useState(true);
	const [trialDays, setTrialDays] = useState("0");

	const mutation = useMutation({
		mutationFn: () =>
			adminClient.createBillingPlan({
				slug,
				name,
				description,
				isPublic,
				trialDays: Number.parseInt(trialDays) || 0,
			}),
		onSuccess: (data) => onSuccess(data.plan?.id ?? ""),
	});

	return (
		<>
			<DialogHeader>
				<DialogTitle>{t("billing_new_plan_title")}</DialogTitle>
			</DialogHeader>
			<div className="space-y-4 pt-2">
				<div className="space-y-2">
					<Label>{t("billing_plan_slug")}</Label>
					<Input
						value={slug}
						onChange={(e) => setSlug(e.target.value)}
						placeholder="pro"
					/>
				</div>
				<div className="space-y-2">
					<Label>{t("billing_plan_name")}</Label>
					<Input
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder={t("billing_plan_name_placeholder")}
					/>
				</div>
				<div className="space-y-2">
					<Label>{t("billing_plan_description")}</Label>
					<Input
						value={description}
						onChange={(e) => setDescription(e.target.value)}
					/>
				</div>
				<div className="space-y-2">
					<Label>{t("billing_trial_days")}</Label>
					<Input
						type="number"
						value={trialDays}
						onChange={(e) => setTrialDays(e.target.value)}
					/>
				</div>
				<div className="flex items-center gap-2">
					<Switch checked={isPublic} onCheckedChange={setIsPublic} />
					<Label>{t("billing_plan_is_public")}</Label>
				</div>
			</div>
			<DialogFooter>
				<Button
					onClick={() => mutation.mutate()}
					disabled={mutation.isPending || !slug || !name}
				>
					{mutation.isPending && (
						<Loader2 className="h-4 w-4 animate-spin mr-1.5" />
					)}
					{t("common:create")}
				</Button>
			</DialogFooter>
		</>
	);
}
